import { useCallback, useEffect, useRef, useState } from "react"
import { fetchTopInfluencers } from "../topInfluencerApi"
import { fetchInfluencerFavourites } from "../influencerFavouritesApi"
import { INFLUENCER_MODE } from "../constants"

export const INFLUENCER_STATUS = {
  LOADING: "loading", // first request, nothing to show yet
  READY: "ready",     // influencers to render
  ERROR: "error",
}

/**
 * Loads a page of either the Top Influencer leaderboard or the calling
 * user's favourites list - the two are the same filter/sort/paging contract
 * against different base paths (INFLUENCER_FAVOURITES_API.md §4: "the saved
 * set becomes one more clause on the same query"), so one hook covers both
 * rather than duplicating it, and InfluencerSection's toolbar toggle is just
 * a `source` prop flip.
 *
 * Much simpler than useUgcVideos - there's no discovery job to poll (either
 * endpoint: "nothing here costs anything", every response is already-settled
 * data), so this is a plain fetch-on-dependency-change. `include_facets` is
 * requested on every call (not once-per-app like the video tabs' facets)
 * since facets narrow along with the filters the user has already applied.
 */
export default function useTopInfluencers({
  source = INFLUENCER_MODE.ALL,
  page = 1,
  pageSize = 20,
  sort = "audience",
  filters = {},
}) {
  const [data, setData] = useState(null)
  const [status, setStatus] = useState(INFLUENCER_STATUS.LOADING)
  const [error, setError] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  const requestTokenRef = useRef(0)

  const load = useCallback(async () => {
    const requestToken = ++requestTokenRef.current
    setIsFetching(true)
    setError(null)
    setStatus(prev => (prev === INFLUENCER_STATUS.READY ? INFLUENCER_STATUS.READY : INFLUENCER_STATUS.LOADING))

    try {
      const fetcher = source === INFLUENCER_MODE.FAVOURITES ? fetchInfluencerFavourites : fetchTopInfluencers
      const result = await fetcher({ page, pageSize, sort, filters, includeFacets: true })
      if (requestToken !== requestTokenRef.current) return // superseded by a newer request

      // Present both endpoints' envelopes identically to callers - the
      // favourites list's rows/counts are keyed `favourites`/`total_favourites`/
      // `total_favourites_unfiltered` (§4) where the ranking uses
      // `influencers`/`total_influencers`/`total_influencers_unfiltered`.
      const normalized = source === INFLUENCER_MODE.FAVOURITES
        ? {
            ...result,
            influencers: result.favourites,
            total_influencers: result.total_favourites,
            total_influencers_unfiltered: result.total_favourites_unfiltered,
          }
        : result

      setData(normalized)
      setStatus(INFLUENCER_STATUS.READY)
    } catch (err) {
      if (requestToken !== requestTokenRef.current) return
      setStatus(INFLUENCER_STATUS.ERROR)
      setError(err.message || "Something went wrong.")
    } finally {
      if (requestToken === requestTokenRef.current) setIsFetching(false)
    }
    // filters is compared by JSON below (same trick as useUgcVideos) rather
    // than listed here directly - its identity changes every render in the
    // caller, but its contents are what should actually trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, page, pageSize, sort, JSON.stringify(filters)])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, page, pageSize, sort, JSON.stringify(filters)])

  return { data, status, error, isFetching, reload: load }
}
